import { buffers, eventChannel } from 'redux-saga';
import {
  actionChannel,
  call,
  delay,
  fork,
  put,
  select,
  take,
} from 'redux-saga/effects';
import tablePayment from '../tablePayment';
import dialogPay from '../dialogs/dialogPay';
import clients from '../clients';
import transactions from '../transactions';
import { ACTION_TYPES } from './pinpad.constant';
import * as actions from './pinpad.action';
import * as selectors from './pinpad.selector';
import { PinpadError, requestAgent } from './pinpad.agent';
import {
  agentState,
  connectPinpad,
  ConnectionContext,
  disconnectPinpad,
  fiscalNow,
} from './pinpad.connection';

const HEALTH_CHECK = 'PDV::PINPAD::HEALTH_CHECK';
export const HEALTH_INTERVAL = 30000;
type Payment = {
  sessionId: string;
  invoice: string;
  fiscal: ReturnType<typeof fiscalNow>;
  installment: any;
  readyToFinish: boolean;
  uncertain: boolean;
  continuationToken: number;
};
type Context = ConnectionContext & {
  payment: Payment | null;
  attemptedInvoices: Set<string>;
  failures: number;
  nextCheck: number;
};

function* idleMessage(context: Context): Generator<any, void, any> {
  if (context.payment || !context.connection) return;
  try {
    const state = yield call(agentState);
    if (
      state.serviceState !== 1 ||
      state.sessionId !== context.connection.sessionId
    )
      return;
    yield call(
      requestAgent,
      '/pinpad/setDisplayMessage',
      'POST',
      {
        sessionId: state.sessionId,
        displayMessage: 'Giro Pagamentos',
        persistent: 'Y',
      },
      [0]
    );
  } catch {
    console.warn('Não foi possível atualizar a mensagem do visor do pinpad.');
  }
}

function* rememberTransaction(context: Context): Generator<any, void, any> {
  const state = yield select(selectors.selectState);
  const session = state.session;
  if (
    session?.sessionId &&
    [
      'transaction_init',
      'transaction_processing',
      'transaction_pending',
    ].includes(state.transaction_status)
  ) {
    context.transaction = {
      sessionId: session.sessionId,
      fiscal: {
        taxInvoiceNumber: session.cupomFiscal,
        taxInvoiceDate: session.dataFiscal,
        taxInvoiceTime: session.horaFiscal,
      },
    };
  }
}

function* connect(
  context: Context,
  background = false
): Generator<any, any, any> {
  const detail = yield select(clients.selector.selectPayload);
  yield call(rememberTransaction, context);
  const wasPresent = yield select(selectors.selectPresent);
  if (!background) {
    yield put(actions.fetchStart());
    yield put(actions.updateStatus('connecting'));
    yield put(actions.updateMessage('Conectando ao pinpad...'));
  }
  const connection = yield call(connectPinpad, context, detail, !background);
  yield put(actions.updateSession({ sessionId: connection.sessionId }));
  yield put(actions.updateTerminal({ terminal: connection.terminal }));
  yield put(actions.updatePresent(true));
  yield put(actions.connectionSuccess());
  if (!background || !wasPresent) yield put(actions.updateStatus('connected'));
  context.failures = 0;
  context.nextCheck = Date.now() + HEALTH_INTERVAL;
  return connection;
}

function* rejected(context: Context, data: any): Generator<any, void, any> {
  context.payment = null;
  yield put(actions.fetchSuccess(data));
  yield put(
    actions.updateMessage(
      data.clisitefStatus > 0
        ? 'Transação negada. Contate o emissor do cartão.'
        : 'Transação não aprovada'
    )
  );
  yield put(transactions.action.servicePatch(10));
  if (data.clisitefStatus === -2) {
    yield call(idleMessage, context);
    yield put(dialogPay.action.close());
  }
}

const nextContinue = (payment: Payment, data: any = '') => ({
  ...actions.servicePostContinue(data),
  operationId: payment.invoice,
  continuationToken: ++payment.continuationToken,
});
const nextFinish = (payment: Payment) => ({
  ...actions.servicePostFinish(undefined),
  operationId: payment.invoice,
});

function* startPayment(context: Context): Generator<any, void, any> {
  if (context.payment) return;
  const transaction = yield select(transactions.selector.selectState);
  const invoice = String(transaction?.data?.meta || '');
  if (!invoice)
    throw new PinpadError(
      'Não foi possível identificar a transação.',
      'configuration'
    );
  if (context.attemptedInvoices.has(invoice)) return;
  const installment = yield select(
    tablePayment.selector.selectInstallmentSelected
  );
  const typeTax = yield select(tablePayment.selector.selectTypeTax);
  const tableAmount = yield select(tablePayment.selector.selectAmount);
  const amount = typeTax === 'ec' ? tableAmount : installment?.amount_number;
  if (!installment || !Number.isFinite(amount) || amount <= 0) {
    throw new PinpadError('Dados do pagamento incompletos.', 'configuration');
  }
  yield put(actions.updateTransactionStatus('transaction_init'));
  const connection = yield call(connect, context);
  const fiscal = fiscalNow(invoice);
  const payment: Payment = {
    sessionId: connection.sessionId,
    invoice,
    fiscal,
    installment,
    readyToFinish: false,
    uncertain: false,
    continuationToken: 0,
  };
  context.payment = payment;
  context.attemptedInvoices.add(invoice);
  yield put(
    actions.updateSession({
      sessionId: payment.sessionId,
      continua: 0,
      ret: [],
      cupomFiscal: invoice,
      dataFiscal: fiscal.taxInvoiceDate,
      horaFiscal: fiscal.taxInvoiceTime,
    })
  );
  yield put(actions.fetchStart());
  const result = yield call(
    requestAgent,
    '/startTransaction',
    'POST',
    {
      sessionId: payment.sessionId,
      functionId:
        installment.type === 'PIX'
          ? 122
          : installment.type === 'Débito'
          ? 2
          : 3,
      trnAmount: (Number(amount.toFixed(2)) * 100).toFixed(0),
      ...fiscal,
      cashierOperator: 'PDV',
      trnAdditionalParameters: '',
    },
    'transaction'
  );
  if (result.clisitefStatus === 10000) {
    yield put(nextContinue(payment));
  } else if (result.clisitefStatus === 0) {
    payment.readyToFinish = true;
    yield put(nextFinish(payment));
  } else {
    yield call(rejected, context, result);
  }
}

function* continuePayment(
  context: Context,
  payload: any
): Generator<any, void, any> {
  const payment = context.payment;
  if (!payment || payment.uncertain || payment.readyToFinish) return;
  const session = yield select(selectors.selectSession);
  if (session?.sessionId !== payment.sessionId)
    throw new PinpadError('A sessão do pagamento mudou.', 'session');
  const sentContinue = session.continua ?? 0;
  yield put(actions.updateTransactionStatus('transaction_processing'));
  yield put(actions.fetchStart());
  const data = yield call(
    requestAgent,
    '/continueTransaction',
    'POST',
    {
      sessionId: payment.sessionId,
      data: payload ?? '',
      continue: sentContinue,
    },
    'transaction',
    60000
  );
  yield put(actions.fetchSuccess(data));
  if (data.clisitefStatus !== 10000) {
    if (data.clisitefStatus === 0) {
      payment.readyToFinish = true;
      yield put(nextFinish(payment));
    } else yield call(rejected, context, data);
    return;
  }
  switch (data.commandId) {
    case 0:
      yield put(
        actions.updateSession({
          ret: [
            ...(session.ret || []),
            { TipoCampo: data.fieldId, Valor: data.data },
          ],
        })
      );
      yield put(nextContinue(payment));
      break;
    case 3:
      if (
        !(
          payment.installment.type === 'PIX' &&
          String(data.data).includes('Aguarde, em processamento')
        )
      ) {
        yield put(actions.updateMessage(data.data));
      }
      yield put(nextContinue(payment));
      break;
    case 1:
    case 2:
    case 4:
    case 11:
    case 12:
    case 13:
    case 14:
    case 15:
    case 16:
      yield put(actions.updateMessage(data.data));
      yield put(nextContinue(payment));
      break;
    case 21:
      yield put(
        nextContinue(payment, payment.installment.installment > 1 ? '2' : '1')
      );
      break;
    case 23:
      // Preserve cancellation requested while the HTTP request was running.
      if (sentContinue === -1)
        yield put(actions.updateSession({ continua: 0 }));
      yield delay(500);
      yield put(nextContinue(payment));
      break;
    case 30:
      if (data.fieldId === 505)
        yield put(nextContinue(payment, payment.installment.installment));
      break;
    default:
      yield put(nextContinue(payment));
  }
}

function* finishPayment(context: Context): Generator<any, void, any> {
  const payment = context.payment;
  if (!payment || payment.uncertain || !payment.readyToFinish) return;
  yield put(actions.fetchStart());
  const data = yield call(
    requestAgent,
    '/finishTransaction',
    'POST',
    {
      sessionId: payment.sessionId,
      confirm: 1,
      ...payment.fiscal,
    },
    [0],
    60000
  );
  yield put(actions.fetchSuccess(data));
  yield put(actions.updateMessage('Transação aprovada'));
  yield put(transactions.action.servicePatch(3));
  context.payment = null;
}

function* connectionFailure(
  context: Context,
  error: any
): Generator<any, void, any> {
  const pending = !!context.payment;
  if (context.payment) context.payment.uncertain = true;
  const message = pending
    ? 'A comunicação do pagamento foi interrompida. Verifique o resultado da transação antes de tentar um novo pagamento.'
    : error.message || 'Não foi possível conectar ao pinpad.';
  if (!pending) {
    context.connection = null;
    yield put(actions.updateSession(null));
    yield put(actions.updateTerminal(null));
  } else {
    yield put(actions.updateTransactionStatus('transaction_pending'));
  }
  yield put(actions.updatePresent(false));
  yield put(
    actions.updateStatus(pending || error.kind === 'busy' ? 'busy' : 'error')
  );
  yield put(actions.updateMessage(message));
  yield put(
    actions.fetchError({ message, kind: error.kind, pending, ...error.details })
  );
  context.failures++;
  context.nextCheck =
    Date.now() +
    Math.min(HEALTH_INTERVAL * 2 ** (context.failures - 1), 120000);
}

function* monitorConnection(): Generator<any, void, any> {
  const events = eventChannel<boolean>((emit) => {
    const timer = setInterval(() => emit(false), HEALTH_INTERVAL);
    const focus = () => {
      if (document.visibilityState !== 'hidden') emit(true);
    };
    window.addEventListener('focus', focus);
    document.addEventListener('visibilitychange', focus);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', focus);
      document.removeEventListener('visibilitychange', focus);
    };
  }, buffers.sliding(1));
  try {
    while (true) {
      const focused = yield take(events);
      yield put({ type: HEALTH_CHECK, focused });
    }
  } finally {
    events.close();
  }
}

function* watch(): Generator<any, void, any> {
  // One queue owns the agent, including the complete identification operation.
  // Payment ownership remains until a definitive result is received.
  const queue = yield actionChannel(
    [...Object.values(ACTION_TYPES.SERVICE), HEALTH_CHECK],
    buffers.expanding()
  );
  const context: Context = {
    connection: null,
    discovery: null,
    payment: null,
    attemptedInvoices: new Set(),
    failures: 0,
    nextCheck: 0,
  };
  yield fork(monitorConnection);
  try {
    while (true) {
      const action = yield take(queue);
      try {
        if (
          action.operationId &&
          action.operationId !== context.payment?.invoice
        )
          continue;
        switch (action.type) {
          case ACTION_TYPES.SERVICE.POST_START:
            yield call(startPayment, context);
            break;
          case ACTION_TYPES.SERVICE.POST_CONTINUE:
            if (context.payment) {
              if (
                action.operationId &&
                action.continuationToken !== context.payment.continuationToken
              )
                break;
              // A manual cancellation supersedes an already queued automatic continuation.
              if (!action.operationId) context.payment.continuationToken++;
            }
            yield call(continuePayment, context, action.payload);
            break;
          case ACTION_TYPES.SERVICE.POST_FINISH:
            yield call(finishPayment, context);
            break;
          case ACTION_TYPES.SERVICE.POST_MESSAGE:
            yield call(idleMessage, context);
            break;
          case ACTION_TYPES.SERVICE.POST_DESTROY_SESSION:
            if (context.payment) break;
            yield call(rememberTransaction, context);
            yield call(disconnectPinpad, context);
            yield put(actions.updateSession(null));
            yield put(actions.updatePresent(false));
            yield put(actions.updateStatus(null));
            break;
          case HEALTH_CHECK: {
            if (
              context.payment ||
              (!action.focused && Date.now() < context.nextCheck)
            )
              break;
            const detail = yield select(clients.selector.selectPayload);
            if (!detail?.cdCliente) break;
            yield call(connect, context, true);
            break;
          }
          case ACTION_TYPES.SERVICE.POST_IS_PRESENT:
          case ACTION_TYPES.SERVICE.POST_NEW_SESSION:
          case ACTION_TYPES.SERVICE.POST_START_INFO:
            if (context.payment) break;
            yield call(connect, context);
            yield call(idleMessage, context);
            break;
          // Discovery continue/finish belong exclusively to connectPinpad.
          default:
            break;
        }
      } catch (error) {
        yield call(connectionFailure, context, error);
        if (
          action.type === ACTION_TYPES.SERVICE.POST_START &&
          !context.payment
        ) {
          yield put(transactions.action.servicePatch(33));
        }
      }
    }
  } finally {
    queue.close();
  }
}

export default { watch };
